import { CircularProgress, IconButton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/pro-light-svg-icons"
import { faSparkles } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"

const useStyles = makeStyles()((theme) => ({
    banner: {
        padding: "1rem",
        borderRadius: ".5rem",
        background: theme.palette.tailwind.purple[800],
        color: theme.palette.common.white,
    },
    icon: {
        "& svg": {
            fontSize: "1rem",
        },
    },
    title: {
        fontWeight: 700,
    },
    actionButton: {
        "&.MuiButton-root": {
            padding: ".625rem 1.25rem",
            background: theme.palette.common.white,
            border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        },
        "&.MuiButton-root:hover": {
            background: theme.palette.tailwind.purple[50],
        },
    },
    regenerateButton: {
        "&.MuiButton-root": {
            color: theme.palette.tailwind.purple[600],
        },
    },
    undoButton: {
        "&.MuiButton-root": {
            color: theme.palette.tailwind.slate[800],
        },
    },
    closeButton: {
        color: theme.palette.common.white,
    },
}))

type Props = Readonly<{
    isWorking: boolean
    templateName: string
    onRegenerate: () => void
    onUndo: () => void
    onClose: () => void
}>

export function AiBanner({ isWorking, templateName, onRegenerate, onUndo, onClose }: Props) {
    const { classes, cx } = useStyles()

    return (
        <Stack direction="row" alignItems="center" spacing="2rem" className={classes.banner}>
            <Stack direction="row" alignItems="center" spacing="1rem" flexGrow={1}>
                <Stack className={classes.icon}>
                    {isWorking ? (
                        <CircularProgress size="1rem" color="inherit" />
                    ) : (
                        <FontAwesomeIcon icon={faSparkles} />
                    )}
                </Stack>
                <Stack spacing=".125rem">
                    {isWorking ? (
                        <>
                            <LocalizedStrict id="merchant-week-menu-ai-banner-working-title">
                                <Typography variant="subtitle1" className={classes.title}>
                                    Generating menu…
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="merchant-week-menu-ai-banner-working-subtitle"
                                vars={{ template: templateName }}
                            >
                                <Typography>Creating dishes for {templateName}</Typography>
                            </LocalizedStrict>
                        </>
                    ) : (
                        <>
                            <LocalizedStrict id="merchant-week-menu-ai-banner-title">
                                <Typography variant="subtitle1" className={classes.title}>
                                    AI generated this menu
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="merchant-week-menu-ai-banner-subtitle">
                                <Typography>Review the dishes before publishing.</Typography>
                            </LocalizedStrict>
                        </>
                    )}
                </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing=".75rem">
                {!isWorking && (
                    <LocalizedStrict id="merchant-week-menu-ai-banner-regenerate-button">
                        <StandardButton
                            fullWidth={false}
                            size="small"
                            className={cx(classes.actionButton, classes.regenerateButton)}
                            onClick={onRegenerate}
                        >
                            Regenerate
                        </StandardButton>
                    </LocalizedStrict>
                )}
                {/* While generating, Undo is the only way out — it cancels the queued day fills. */}
                <LocalizedStrict id="merchant-week-menu-ai-banner-undo-button">
                    <StandardButton
                        fullWidth={false}
                        size="small"
                        className={cx(classes.actionButton, classes.undoButton)}
                        onClick={onUndo}
                    >
                        Undo
                    </StandardButton>
                </LocalizedStrict>
                {!isWorking && (
                    <LocalizedStrict id="merchant-week-menu-ai-banner-close-button" attrs={{ "aria-label": true }}>
                        <IconButton size="small" aria-label="Close" className={classes.closeButton} onClick={onClose}>
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                    </LocalizedStrict>
                )}
            </Stack>
        </Stack>
    )
}
